// Environment configuration
const envFromImportMeta = (typeof import.meta !== 'undefined' ? (import.meta as any).env : {}) || {}
const envFromProcess = (typeof process !== 'undefined' ? (process as any).env : {}) || {}
const env = { ...envFromProcess, ...envFromImportMeta }
const isBrowserRuntime = typeof window !== 'undefined'
const isLocalBrowserHost =
  isBrowserRuntime &&
  (window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1' ||
    window.location.hostname === '::1')
const isProdMode =
  env.MODE === 'production' ||
  env.NODE_ENV === 'production' ||
  (isBrowserRuntime && !isLocalBrowserHost)
const configuredApiBaseUrl = env.BUN_PUBLIC_API_BASE_URL
const apiBaseUrl =
  configuredApiBaseUrl !== undefined
    ? configuredApiBaseUrl
    : isProdMode
      ? 'https://3d.jensen-beds.com'
      : 'http://localhost:4444'

export const config = {
  // API Configuration
  api: {
    // In production, use empty string for relative URLs (Caddy proxies /api/* to backend)
    // In dev, use http://localhost:4444 (backend port)
    baseUrl: apiBaseUrl,
    version: env.BUN_PUBLIC_API_VERSION || 'v1',
    getUrl: (endpoint: string) => {
      const base = config.api.baseUrl
      // Handle relative URLs (production) vs absolute URLs (dev)
      if (!base || base === '') {
        return `/api/${config.api.version}/${endpoint}`
      }
      return `${base}/api/${config.api.version}/${endpoint}`
    },
  },
  
  // Environment
  isDevelopment: !isProdMode,
  isProduction: isProdMode,
  
  // Feature flags
  enableDebug: env.BUN_PUBLIC_ENABLE_DEBUG === 'true' || false,
} as const

// Entrypoint helpers
export const isScreenshotEntrypoint = (): boolean => {
  if (typeof window === 'undefined') return false
  return window.location.pathname.startsWith('/screenshot')
}

// Helper function to build full API URLs
export const buildApiUrl = (endpoint: string): string => {
  return config.api.getUrl(endpoint)
}

// Helper function to normalize URLs - match the current page protocol (http/https)
const normalizeUrl = (url: string): string => {
  if (!url) return ''
  // Only normalize fully-qualified http/https URLs; leave relative URLs alone
  if (typeof window === 'undefined') return url
  const protocol = window.location.protocol
  if (protocol !== 'http:' && protocol !== 'https:') return url

  // Protocol-relative URL (e.g. //example.com/...) – just prefix current protocol
  if (url.startsWith('//')) {
    return `${protocol}${url.slice(1)}`
  }

  // If it's not an absolute http/https URL, don't touch it
  if (!/^https?:\/\//i.test(url)) return url

  try {
    const u = new URL(url)
    u.protocol = protocol
    return u.toString()
  } catch {
    return url
  }
}

// Helper function to build full image URLs
export const buildImageUrl = (path: string): string => {
  if (!path) return ''
  // Normalize HTTP to HTTPS if needed
  if (path.startsWith('http')) {
    return normalizeUrl(path)
  }
  const base = config.api.baseUrl
  // Handle relative URLs (production) vs absolute URLs (dev)
  if (!base || base === '') {
    // Relative URL - just return the path as-is or prepend /
    if (path.startsWith('/')) return path
    return `/${path}`
  }
  // Absolute URL
  if (path.startsWith('/')) return `${base}${path}`
  return `${base}/${path}`
}

// Helper function to normalize model/asset URLs (for FBX, textures, etc.)
export const normalizeAssetUrl = (url: string): string => {
  if (!url) return ''
  return normalizeUrl(url)
}
